import { useMemo, useCallback } from "react";
import { SelectOptionType } from "@aidkitorg/component-library";
import { useSettings } from "../../Contexts/SettingsContext";

export interface UseFieldOptionsConfig {
  /**
   * Filter function to include/exclude specific fields
   */
  fieldFilter?: (field: any) => boolean;
  /**
   * Custom label function - return display label for field
   */
  getLabelForField?: (field: any) => string;
}

export const useFieldOptions = (config: UseFieldOptionsConfig = {}) => {
  const { fields } = useSettings();
  
  const {
    fieldFilter = () => true,
    getLabelForField = (field) => field.displayName || field.id
  } = config;

  // Create flat field options
  const fieldOptions: SelectOptionType[] = useMemo(() => {
    if (!fields || fields.length === 0) return [];

    // Filter and map fields to flat options
    return fields
      .filter(fieldFilter)
      .map(field => ({
        value: field.id,
        label: getLabelForField(field)
      }));
  }, [fields, fieldFilter, getLabelForField]);

  // Helper to get selected field option
  const getSelectedFieldOption = useCallback((fieldId: string): SelectOptionType | undefined => {
    if (!fieldId || !fields) return undefined;
    
    const field = fields.find(f => f.id === fieldId);
    if (!field) return undefined;
    
    return {
      value: field.id,
      label: getLabelForField(field)
    };
  }, [fields, getLabelForField]);

  // Helper to get field by ID
  const getFieldById = useCallback((fieldId: string) => {
    return fields?.find(f => f.id === fieldId);
  }, [fields]);

  return {
    fieldOptions,
    getSelectedFieldOption,
    getFieldById,
    fields: fields || []
  };
};